/**
 * useActivityStatusUpdate — THE canonical activity-status update hook
 * (card l_Q1_9Vr_d8r — consolidation refactor).
 *
 * Every DESKTOP entry point that changes an activity's status routes through
 * this ONE hook: the clickable status badge, the Activities/Production detail
 * sidebars, the edit-lot-activity modal, the PO-tracker inline popover, and the
 * batch toolbars. It replaces two competing hooks that each fanned out broad
 * invalidations and caused the ~10s "invalidation storm" (see
 * activityStatusCachePatch.ts for the full root-cause writeup).
 *
 * Contract:
 *  - onMutate: optimistically patch status_id + trig_activity_status_name +
 *    trig_activity_status_color (+ completion fields when terminal) onto
 *    activitiesKeys.detail(id) AND activitiesKeys.list({ trig_area_lot_id }).
 *  - onError: roll back to the snapshot + toast.
 *  - onSettled: SCOPED, non-blocking invalidation of ONLY detail(id) +
 *    list({ trig_area_lot_id }). NEVER .all / ['activities'] / ['job-tracker']
 *    / ['job-activity-feed'] / ['lot-activities'] / dashboard workflows.
 *  - Auto-complete detection runs in the BACKGROUND (not awaited).
 *  - No double-fire.
 *
 * The FIELD path keeps its own offline useFieldMutation transport but shares
 * the same activityStatusCachePatch helpers, so cache semantics are identical.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import { useCallback } from 'react';

import { notifications } from '../../../../config/toast-notifications.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { activitiesExtensions } from '../../extensions/activities-business-logic-extension.js';
import { activityStatusExtensions } from '../../extensions/activity-status-business-logic-extension.js';
import type { ActivityStatusBusinessResponse } from '../../extensions/activity-status-business-logic-extension.js';
import type { Activities, UpdateActivities } from '../../generated_schemas/generated-activities-schema.js';

import {
  applyActivityStatusPatch,
  invalidateActivityStatusScoped,
  rollbackActivityStatusPatch,
  type ActivityStatusSnapshot,
} from './activityStatusCachePatch.js';

// Query keys for the org-scoped available-statuses query. Kept identical to the
// legacy hooks' keys so the cached statuses list is shared (no extra fetch).
export const activityStatusManagementKeys = {
  all: ['activity-status-management'] as const,
  byOrganization: (orgId: string) => [...activityStatusManagementKeys.all, 'by-organization', orgId] as const,
  default: (orgId: string) => [...activityStatusManagementKeys.all, 'default', orgId] as const,
};

export interface UpdateActivityStatusParams {
  activityId: string;
  statusId: string;
  /** Whether the activity has user assignments — determines square_footage_claimed on completion. */
  isAssigned?: boolean;
  /**
   * The activity's lot id, so we optimistically patch the lot's list entry and
   * scope the reconcile invalidation. Optional; when absent only the detail is
   * patched/invalidated.
   */
  lotId?: string | null;
  /**
   * Optional full activity — enables background auto-complete detection when the
   * target status is terminal. Passing it does NOT change the write.
   */
  activity?: Activities | null;
}

export interface ActivityStatusUpdateResult {
  availableStatuses: ActivityStatusBusinessResponse[] | undefined;
  isLoadingStatuses: boolean;
  statusesError: Error | null;
  updateActivityStatus: (params: UpdateActivityStatusParams) => Promise<Activities>;
  isUpdatingStatus: boolean;
  updateError: Error | null;
  refetchStatuses: () => void;
}

interface MutationContext {
  snapshot: ActivityStatusSnapshot;
  lotId?: string | null;
}

/**
 * Canonical hook for activity-status operations.
 *
 * @param options.onAutoCompleteCandidates — optional callback invoked (in the
 *   background, after the write settles) when a terminal status change reveals
 *   earlier activities eligible for auto-completion. The badge uses this to open
 *   its confirmation dialog. Consumers that don't care omit it.
 */
export function useActivityStatusUpdate(options?: {
  onAutoCompleteCandidates?: (activity: Activities, targetStatus: ActivityStatusBusinessResponse) => void;
}): ActivityStatusUpdateResult {
  const queryClient = useQueryClient();
  const { organizationId, user } = useAuth();

  const {
    data: statusesResponse,
    isLoading: isLoadingStatuses,
    error: statusesError,
    refetch: refetchStatuses,
  } = useQuery({
    queryKey: activityStatusManagementKeys.byOrganization(organizationId || ''),
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return activityStatusExtensions.getActivityStatusesByOrganization(organizationId);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!organizationId,
  });

  const availableStatuses = statusesResponse?.data;

  const mutation = useMutation<Activities, Error, UpdateActivityStatusParams, MutationContext>({
    mutationFn: async ({ activityId, statusId, isAssigned }) => {
      const newStatus = availableStatuses?.find((s) => s.id === statusId);
      const isTerminalStatus = newStatus?.is_terminal === true;

      const updateData: Partial<UpdateActivities> = {
        status_id: statusId,
      };
      if (isTerminalStatus) {
        // Local midnight today — the UTC date part is tomorrow after ~6 PM in the US.
        updateData.completed_date = startOfDay(new Date()).toISOString();
        updateData.completed_by = user?.id || null;
        updateData.square_footage_claimed = isAssigned ?? false;
        updateData.was_autocompleted = false;
      } else {
        updateData.completed_date = null;
        updateData.completed_by = null;
        updateData.square_footage_claimed = null;
        updateData.was_autocompleted = null;
      }

      return activitiesExtensions.update(activityId, updateData);
    },
    // OPTIMISTIC: patch the cache synchronously so the badge/label flips in
    // <100ms — the UI never waits on a refetch (the storm's root cause).
    onMutate: async ({ activityId, statusId, isAssigned, lotId }) => {
      const newStatus = availableStatuses?.find((s) => s.id === statusId);
      const snapshot = applyActivityStatusPatch(queryClient, {
        activityId,
        statusId,
        statusName: newStatus?.name ?? null,
        statusColor: newStatus?.color ?? null,
        isComplete: newStatus?.is_terminal === true,
        completedBy: user?.id || null,
        squareFootageClaimed: newStatus?.is_terminal === true ? isAssigned ?? false : null,
        wasAutocompleted: newStatus?.is_terminal === true ? false : null,
        lotId,
      });
      return { snapshot, lotId };
    },
    onError: (_error, _params, context) => {
      if (context?.snapshot) {
        rollbackActivityStatusPatch(queryClient, context.snapshot);
      }
      notifications.error('Error', 'Failed to update activity status. Please try again.');
    },
    // SCOPED, non-blocking reconcile — ONLY the two keys this write touched.
    onSettled: (_data, _error, params, context) => {
      invalidateActivityStatusScoped(queryClient, params.activityId, context?.lotId ?? params.lotId);
    },
  });

  const updateActivityStatus = useCallback(
    async (params: UpdateActivityStatusParams): Promise<Activities> => {
      const result = await mutation.mutateAsync(params);

      // BACKGROUND auto-complete detection — never awaited, never blocks the
      // caller. Only fires when the target status is terminal, a consumer wants
      // candidates, and we have the full activity to scope by area/lot.
      const newStatus = availableStatuses?.find((s) => s.id === params.statusId);
      if (newStatus?.is_terminal === true && params.activity && options?.onAutoCompleteCandidates) {
        // Defer to a microtask so this genuinely runs after the caller's own
        // optimistic UI updates settle.
        queueMicrotask(() => {
          try {
            options.onAutoCompleteCandidates!(params.activity!, newStatus);
          } catch {
            // Detection is best-effort; never surface its failure to the write.
          }
        });
      }

      return result;
    },
    [mutation, availableStatuses, options],
  );

  return {
    availableStatuses,
    isLoadingStatuses,
    statusesError: statusesError as Error | null,
    updateActivityStatus,
    isUpdatingStatus: mutation.isPending,
    updateError: mutation.error,
    refetchStatuses: () => refetchStatuses(),
  };
}

/**
 * Hook for getting the default activity status for the current organization.
 * (Migrated from the deleted duplicate hooks so their single consumer surface
 * stays intact.)
 */
export function useDefaultActivityStatus() {
  const { organizationId } = useAuth();

  return useQuery({
    queryKey: activityStatusManagementKeys.default(organizationId || ''),
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return activityStatusExtensions.getDefaultActivityStatus(organizationId);
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: !!organizationId,
  });
}
